import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicTranscript } from '../../lib/api/epics';
import type {
  TranscriptMessage,
  TranscriptContentBlock,
} from '../../../../shared/types/epics';
import { StatusBadge } from '../ui';
import { downloadTranscript } from '../../lib/transcriptDownload';
import { agentEmoji } from '../../lib/agentEmoji';
import { SessionLogViewer } from '../sessions/SessionLogViewer';
import { sessionLogsHref, taskIdFromArn } from '../../lib/sessions/links';

interface TranscriptDrawerProps {
  open: boolean;
  onClose: () => void;
  epicId: number | string;
  storyId: string;
  agentType: string;
  /** FORGE-194: run selector — resolves the exact retry's checkpoint. */
  taskArn?: string | null;
}

type DrawerTab = 'transcript' | 'logs';

/**
 * Slide-in drawer that renders the agent's full conversation transcript
 * from the S3 checkpoint. Anthropic message blocks (text / tool_use /
 * tool_result) are rendered with distinct styling so the operator can
 * scan a long log quickly.
 *
 * UX-04: also hosts a "Raw logs" tab — the same CloudWatch viewer the
 * `/session-logs` page uses — so operators don't have to leave the drawer
 * to see the container's raw stdout/stderr. Raw logs only makes sense once
 * a task has actually run, so the tab is disabled without a `taskArn`.
 */
export const TranscriptDrawer: React.FC<TranscriptDrawerProps> = ({
  open,
  onClose,
  epicId,
  storyId,
  agentType,
  taskArn,
}) => {
  const [tab, setTab] = useState<DrawerTab>('transcript');

  // Reset to the Transcript tab each time the drawer is opened for a
  // (possibly different) run, so "Raw logs" never leaks across runs.
  useEffect(() => {
    if (open) setTab('transcript');
  }, [open, storyId, agentType, taskArn]);

  const query = useQuery({
    queryKey: ['transcript', String(epicId), storyId, agentType, taskArn ?? null],
    queryFn: () => fetchEpicTranscript({ epicId, storyId, agentType, taskArn }),
    enabled: open && tab === 'transcript',
    staleTime: 60_000,
  });

  // Close on ESC.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const taskId = taskArn ? taskIdFromArn(taskArn) : null;
  const logsHref = taskId
    ? sessionLogsHref({ task: taskId, agent: agentType, story: storyId })
    : null;

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true">
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs"
        onClick={onClose}
      />
      <aside
        data-testid="transcript-drawer"
        className="absolute right-0 top-0 bottom-0 w-full sm:max-w-3xl bg-white shadow-xl
                   border-l border-slate-200 flex flex-col"
      >
        <header className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              Transcript
            </div>
            <h2 className="text-base font-semibold text-slate-900 truncate">
              <span aria-hidden="true">{agentEmoji(agentType)}</span>{' '}
              {agentType}{' '}
              <span className="text-slate-400 font-normal">·</span>{' '}
              <span className="font-mono text-sm">{storyId}</span>
            </h2>
            {tab === 'transcript' && query.data && (
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
                <StatusBadge tone="neutral">
                  iter {query.data.iteration}
                </StatusBadge>
                {query.data.reason && (
                  <StatusBadge
                    tone={
                      query.data.reason === 'complete' ? 'success' : 'warning'
                    }
                  >
                    {query.data.reason}
                  </StatusBadge>
                )}
                <span className="font-mono">
                  {query.data.messages.length} msgs · {formatBytes(query.data.bytes)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {tab === 'transcript' && query.data && query.data.messages.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => downloadTranscript(query.data!, 'md')}
                  title="Download transcript as Markdown"
                  className="rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 px-2 py-1 text-xs font-medium"
                >
                  ↓ .md
                </button>
                <button
                  type="button"
                  onClick={() => downloadTranscript(query.data!, 'json')}
                  title="Download transcript as raw JSON"
                  className="rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 px-2 py-1 text-xs font-medium"
                >
                  ↓ .json
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close transcript"
              className="rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 p-1.5"
            >
              ✕
            </button>
          </div>
        </header>

        <nav role="tablist" className="border-b border-slate-200 flex gap-1 px-5 bg-white">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'transcript'}
            data-testid="transcript-drawer-tab-transcript"
            onClick={() => setTab('transcript')}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px transition-colors duration-150 ' +
              (tab === 'transcript'
                ? 'border-primary-500 text-primary-600 font-semibold'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-200')
            }
          >
            Transcript
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'logs'}
            disabled={!taskId}
            title={!taskId ? 'No task recorded for this run yet' : undefined}
            data-testid="transcript-drawer-tab-logs"
            onClick={() => setTab('logs')}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px transition-colors duration-150 ' +
              (tab === 'logs'
                ? 'border-primary-500 text-primary-600 font-semibold'
                : !taskId
                ? 'border-transparent text-slate-300 cursor-not-allowed'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-200')
            }
          >
            Raw logs
          </button>
        </nav>

        {tab === 'transcript' ? (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50">
            {query.isLoading && (
              <div className="text-sm text-slate-500">Loading transcript…</div>
            )}
            {query.isError && (
              <div
                role="alert"
                className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2"
              >
                {(query.error as Error).message === 'NOT_FOUND'
                  ? `No transcript stored for ${agentType} on story ${storyId} yet.`
                  : (query.error as Error).message}
              </div>
            )}
            {query.isSuccess && query.data.messages.length === 0 && (
              <div className="text-sm text-slate-500 italic">
                The checkpoint exists but contains no messages.
              </div>
            )}
            {query.isSuccess &&
              query.data.messages.map((msg, i) => (
                <MessageBlock key={i} message={msg} />
              ))}
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            {taskId && (
              <SessionLogViewer
                taskId={taskId}
                toolbarExtra={
                  logsHref ? (
                    <Link
                      to={logsHref}
                      className="text-xs text-slate-300 hover:text-white hover:underline whitespace-nowrap ml-auto"
                    >
                      Open full page ↗
                    </Link>
                  ) : undefined
                }
              />
            )}
          </div>
        )}

        {tab === 'transcript' && query.data && (
          <footer className="px-5 py-2 text-2xs text-slate-500 border-t border-slate-200 font-mono break-all">
            {query.data.s3Key}
          </footer>
        )}
      </aside>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Message + content-block rendering
// ---------------------------------------------------------------------------

const MessageBlock: React.FC<{ message: TranscriptMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  const blocks = normaliseContent(message.content);

  return (
    <article
      className={
        'rounded-lg border ' +
        (isUser
          ? 'border-slate-200 bg-white'
          : 'border-primary-100 bg-primary-50/40')
      }
    >
      <header
        className={
          'px-3 py-1.5 text-2xs uppercase tracking-wide font-semibold border-b ' +
          (isUser
            ? 'border-slate-200 text-slate-600'
            : 'border-primary-100 text-primary-700')
        }
      >
        {message.role}
      </header>
      <div className="px-3 py-2 space-y-2 text-sm">
        {blocks.map((b, i) => (
          <ContentBlockView key={i} block={b} />
        ))}
      </div>
    </article>
  );
};

function normaliseContent(content: TranscriptMessage['content']): TranscriptContentBlock[] {
  if (typeof content === 'string') {
    return [{ type: 'text', text: content }];
  }
  if (Array.isArray(content)) return content;
  return [];
}

const ContentBlockView: React.FC<{ block: TranscriptContentBlock }> = ({ block }) => {
  switch (block.type) {
    case 'text':
      return (
        <div className="text-slate-800 whitespace-pre-wrap">{block.text}</div>
      );
    case 'thinking':
      return (
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer text-slate-500 italic">
            thinking
          </summary>
          <pre className="mt-1 whitespace-pre-wrap font-mono text-2xs">
            {block.thinking}
          </pre>
        </details>
      );
    case 'tool_use':
      return (
        <div className="border-l-2 border-purple-500 pl-2">
          <div className="text-2xs uppercase tracking-wide text-purple-600 font-semibold">
            tool · {block.name}
          </div>
          <pre className="mt-1 whitespace-pre-wrap break-all text-2xs font-mono text-slate-700 bg-white border border-slate-200 rounded p-2 max-h-64 overflow-y-auto">
            {safeStringify(block.input)}
          </pre>
        </div>
      );
    case 'tool_result':
      return (
        <div
          className={
            'border-l-2 pl-2 ' +
            (block.is_error ? 'border-danger-500' : 'border-accent-500')
          }
        >
          <div
            className={
              'text-2xs uppercase tracking-wide font-semibold ' +
              (block.is_error ? 'text-danger-700' : 'text-accent-600')
            }
          >
            tool_result{block.is_error ? ' (error)' : ''}
          </div>
          <pre className="mt-1 whitespace-pre-wrap break-all text-2xs font-mono text-slate-700 bg-white border border-slate-200 rounded p-2 max-h-64 overflow-y-auto">
            {typeof block.content === 'string'
              ? block.content
              : safeStringify(block.content)}
          </pre>
        </div>
      );
    case 'image':
      return (
        <div className="text-2xs text-slate-500 italic">
          [image · {block.source.media_type ?? 'unknown'}]
        </div>
      );
    default:
      return (
        <pre className="text-2xs text-slate-500 font-mono">
          {safeStringify(block)}
        </pre>
      );
  }
};

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default TranscriptDrawer;
